type Comparator<T, R> = {
    selector: (element: T) => R;
    compare?: (a: R, b: R) => number;
    order?: 'asc' | 'desc';
};

export const naturalOrder = <T>(a: T, b: T): number => (a < b ? -1 : a > b ? 1 : 0);

const compareNulls = <T>(a: T, b: T, nullsPosition: 'first' | 'last'): number | null => {
    if (a == null && b == null) {
        return 0;
    } else if (a == null) {
        return nullsPosition === 'first' ? -1 : 1;
    } else if (b == null) {
        return nullsPosition === 'first' ? 1 : -1;
    }
    return null; // Both are non-null, continue with regular comparison
};

export const comparator =
    <T, R>(myComparator: Comparator<T, R>) =>
    (a: T, b: T): number => {
        const a2 = myComparator.selector(a);
        const b2 = myComparator.selector(b);

        const compareFn = myComparator.compare ?? naturalOrder;

        return myComparator.order === 'desc' ? -compareFn(a2, b2) : compareFn(a2, b2);
    };

export const keyComparator = <T, R>(
    selector: (element: T) => R,
    order: 'asc' | 'desc' = 'asc',
    nullsPosition?: 'first' | 'last',
): ((a: T, b: T) => number) => {
    // Smart default: nulls last for asc, nulls first for desc
    const effectiveNullsPosition = nullsPosition ?? (order === 'asc' ? 'last' : 'first');

    return (a: T, b: T): number => {
        const a2 = selector(a);
        const b2 = selector(b);

        // Handle nulls first, before any order considerations
        const nullResult = compareNulls(a2, b2, effectiveNullsPosition);
        if (nullResult !== null) {
            return nullResult;
        }

        // For non-null values, apply normal comparison with order
        const result = naturalOrder(a2, b2);
        return order === 'desc' ? -result : result;
    };
};

const localeComparator = <T>(
    selector: (element: T) => string,
    options: Intl.CollatorOptions,
    order?: 'asc' | 'desc',
): ((a: T, b: T) => number) =>
    comparator({
        selector,
        compare: (a, b) => a.localeCompare(b, undefined, options),
        order,
    });

export const numericLocaleComparator = <T>(
    selector: (element: T) => string,
    order?: 'asc' | 'desc',
): ((a: T, b: T) => number) => localeComparator(selector, {numeric: true}, order);

export const caseInsensitiveComparator = <T>(
    selector: (element: T) => string,
    order: 'asc' | 'desc' = 'asc',
): ((a: T, b: T) => number) => localeComparator(selector, {sensitivity: 'base'}, order);

export const composeComparators =
    <T>(comparators: ((a: T, b: T) => number)[]) =>
    (a: T, b: T): number => {
        for (const comparatorFn of comparators) {
            const result = comparatorFn(a, b);

            if (result !== 0) {
                return result;
            }
        }

        return 0;
    };

export const sortOnKey =
    <T, R>(selector: (element: T) => R): ((items: T[]) => T[]) =>
    (items: T[]) =>
        items.sort((a, b) => naturalOrder(selector(a), selector(b)));

type ProBraLocType = {
    propertyNo: string | null;
    branchNo: string | null;
    locationNo: string | null;
};

export const proBraLocComparator = (
    order: 'asc' | 'desc',
): ((a: ProBraLocType, b: ProBraLocType) => number) =>
    composeComparators<ProBraLocType>([
        numericLocaleComparator(obj => obj.propertyNo ?? '', order),
        numericLocaleComparator(obj => obj.branchNo ?? '', order),
        numericLocaleComparator(obj => obj.locationNo ?? '', order),
    ]);
